import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2 } from "lucide-react";
import { api } from "../lib/api";

// Global admin search — searches across posts, custom pages, page tiles
// (every page_key), nav items, polls, squad/classic squad players, and
// products, then jumps straight to the right admin edit screen. Built
// specifically so a tile living under an unexpected page_key (e.g. a
// duplicate under "page:merch2") is easy to find without opening every
// page one by one.
export default function AdminGlobalSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  useEffect(() => {
    const term = query.trim();
    if (!term) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const handle = setTimeout(() => {
      api
        .get("/admin/global-search", { params: { q: term } })
        .then((r) => setResults(Array.isArray(r.data) ? r.data : []))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(handle);
  }, [query]);

  const goTo = (url) => {
    setOpen(false);
    setQuery("");
    setResults([]);
    navigate(url);
  };

  return (
    <div className="relative w-full max-w-xs" ref={boxRef} data-testid="admin-global-search">
      <div className="relative">
        <Search
          size={14}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--brand-body)]"
        />
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search everything…"
          data-testid="admin-global-search-input"
          className="w-full rounded-md border border-black/10 bg-white py-2 pl-8 pr-8 text-sm outline-none focus:border-[#0A0F17]"
        />
        {loading ? (
          <Loader2
            size={14}
            className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-[var(--brand-body)]"
          />
        ) : null}
      </div>

      {open && query.trim() ? (
        <div
          className="absolute left-0 right-0 top-full z-50 mt-1 max-h-96 overflow-y-auto rounded-md border border-black/10 bg-white shadow-xl scrollbar-thin"
          data-testid="admin-global-search-results"
        >
          {results.length === 0 && !loading ? (
            <p className="px-3 py-3 text-sm text-[var(--brand-body)]" data-testid="admin-global-search-empty">
              No matches for &quot;{query.trim()}&quot;.
            </p>
          ) : (
            results.map((r, i) => (
              <button
                key={`${r.type}-${i}`}
                type="button"
                onClick={() => goTo(r.url)}
                data-testid={`admin-global-search-result-${i}`}
                className="flex w-full flex-col items-start gap-0.5 border-b border-black/5 px-3 py-2 text-left text-sm hover:bg-[#6CABDD]/10 last:border-b-0"
              >
                <span className="font-semibold text-[var(--brand-ink)]">{r.label}</span>
                <span className="label-kicker text-[var(--brand-body)]">{r.hint}</span>
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
